// src/pages/ROICalculator/calculationUtils.js

import { CALCULATION_CONFIG, getIndustryAvgTransaction, getIndustrySocialPosts } from './constants';

/**
 * Calculate Time Savings
 */
export const calculateTimeSavings = (messagesPerDay, currentReviews) => {
  const config = CALCULATION_CONFIG;

  // Customer Service Time Savings
  const dailyCustomerServiceMinutes = 
    messagesPerDay * config.customerService.averageResponseTime * config.customerService.aiHandlingRate;
  const dailyCustomerServiceHours = dailyCustomerServiceMinutes / 60;
  const weeklyCustomerServiceHours = dailyCustomerServiceHours * 6; // 6 business days
  const monthlyCustomerServiceHours = weeklyCustomerServiceHours * 4.33;
  const annualCustomerServiceHours = monthlyCustomerServiceHours * 12;

  // Content Creation Time Savings
  const weeklyContentHours = config.contentCreation.baseWeeklyHours * config.contentCreation.aiReductionRate;
  const monthlyContentHours = weeklyContentHours * 4.33;
  const annualContentHours = monthlyContentHours * 12;

  // Review Management Time Savings
  const monthlyNewReviews = Math.max(1, currentReviews / 12);
  const monthlyReviewMinutes = 
    monthlyNewReviews * config.reviewManagement.responseTime * config.reviewManagement.aiAssistanceRate;
  const monthlyReviewHours = monthlyReviewMinutes / 60;
  const annualReviewHours = monthlyReviewHours * 12;

  // Totals
  const totalWeeklyHours = weeklyCustomerServiceHours + weeklyContentHours + (monthlyReviewHours / 4.33);
  const totalMonthlyHours = monthlyCustomerServiceHours + monthlyContentHours + monthlyReviewHours;
  const totalAnnualHours = annualCustomerServiceHours + annualContentHours + annualReviewHours;

  return {
    customerService: {
      daily: dailyCustomerServiceHours,
      weekly: weeklyCustomerServiceHours,
      monthly: monthlyCustomerServiceHours,
      annual: annualCustomerServiceHours
    },
    content: {
      weekly: weeklyContentHours,
      monthly: monthlyContentHours,
      annual: annualContentHours
    },
    reviews: {
      monthly: monthlyReviewHours,
      annual: annualReviewHours
    },
    total: {
      weekly: totalWeeklyHours,
      monthly: totalMonthlyHours,
      annual: totalAnnualHours
    }
  };
};

/**
 * Calculate Cost Savings
 */
export const calculateCostSavings = (monthlyHoursSaved) => {
  const config = CALCULATION_CONFIG;

  // Labor Cost Savings
  const hourlyLaborCost = config.laborCosts.partTimeHourlyRate * config.laborCosts.benefitsMultiplier;
  const monthlyLaborSavings = monthlyHoursSaved * hourlyLaborCost;
  const annualLaborSavings = monthlyLaborSavings * 12;

  // Tool Cost Savings (vs buying separate tools)
  const monthlyToolSavings = config.toolCosts.traditionalMonthly - config.toolCosts.revferPremium;
  const annualToolSavings = monthlyToolSavings * 12;

  // Total Savings
  const totalMonthlySavings = monthlyLaborSavings + monthlyToolSavings;
  const totalAnnualSavings = annualLaborSavings + annualToolSavings;

  return {
    labor: {
      hourlyRate: hourlyLaborCost,
      monthly: monthlyLaborSavings,
      annual: annualLaborSavings
    },
    tools: {
      monthly: monthlyToolSavings,
      annual: annualToolSavings
    },
    total: {
      monthly: totalMonthlySavings,
      annual: totalAnnualSavings
    }
  };
};

/**
 * Estimate Smart Rating based on current rating and review count
 */
export const estimateSmartRating = (currentRating, reviewCount) => {
  const config = CALCULATION_CONFIG.smartRatingBoost;

  // Confidence factor based on review count (more reviews = more reliable estimate)
  const confidenceFactor = Math.min(reviewCount / 100, 1);

  // Determine potential boost based on current rating
  let potentialBoost;
  if (currentRating >= config.high.threshold) {
    potentialBoost = config.high.boost;
  } else if (currentRating >= config.good.threshold) {
    potentialBoost = config.good.boost;
  } else if (currentRating >= config.moderate.threshold) {
    potentialBoost = config.moderate.boost;
  } else {
    potentialBoost = config.low.boost;
  }

  // Adjust boost based on confidence (fewer reviews = less confident in improvement)
  const adjustedBoost = potentialBoost * (0.5 + 0.5 * confidenceFactor);

  // Calculate Smart Rating (cap at 5.0)
  const smartRating = Math.min(currentRating + adjustedBoost, 5.0);

  return {
    smartRating: parseFloat(smartRating.toFixed(1)),
    boost: parseFloat(adjustedBoost.toFixed(1)),
    confidenceFactor: parseFloat(confidenceFactor.toFixed(2))
  };
};

/**
 * Calculate Revenue Impact
 */
export const calculateRevenueImpact = (monthlyRevenue, currentRating, reviewCount, messagesPerDay, industry) => {
  const config = CALCULATION_CONFIG.revenueImpact;

  // Smart Rating Impact
  const smartRatingData = estimateSmartRating(currentRating, reviewCount);
  const ratingIncrease = smartRatingData.boost;
  const monthlyRatingRevenue = monthlyRevenue * ratingIncrease * config.ratingImpactPerStar;
  const annualRatingRevenue = monthlyRatingRevenue * 12;

  // Faster Response Time Impact
  const avgTransaction = getIndustryAvgTransaction(industry);
  const monthlyCustomers = monthlyRevenue / avgTransaction;
  const monthlyInquiries = messagesPerDay * 30;
  
  // Conservative conversion rate estimate
  const baseConversionRate = Math.min(monthlyCustomers / monthlyInquiries, 0.30);
  
  // Additional customers from faster response time
  const additionalCustomers = monthlyInquiries * baseConversionRate * config.responseTimeConversionBoost;
  const monthlyResponseRevenue = additionalCustomers * avgTransaction;
  const annualResponseRevenue = monthlyResponseRevenue * 12;

  // Network Discovery Potential (marked as potential)
  const monthlyNetworkRevenue = monthlyRevenue * config.networkDiscoveryBoost;
  const annualNetworkRevenue = monthlyNetworkRevenue * 12;

  // Totals (excluding network discovery from main total as it's potential)
  const totalMonthlyRevenue = monthlyRatingRevenue + monthlyResponseRevenue;
  const totalAnnualRevenue = annualRatingRevenue + annualResponseRevenue;

  return {
    rating: {
      currentRating,
      smartRating: smartRatingData.smartRating,
      increase: ratingIncrease,
      monthly: monthlyRatingRevenue,
      annual: annualRatingRevenue
    },
    response: {
      additionalCustomers: Math.round(additionalCustomers),
      monthly: monthlyResponseRevenue,
      annual: annualResponseRevenue
    },
    network: {
      monthly: monthlyNetworkRevenue,
      annual: annualNetworkRevenue,
      isPotential: true
    },
    total: {
      monthly: totalMonthlyRevenue,
      annual: totalAnnualRevenue
    }
  };
};

/**
 * Calculate Total ROI
 */
export const calculateTotalROI = (costSavings, revenueImpact) => {
  const annualRevferCost = CALCULATION_CONFIG.toolCosts.revferPremium * 12;

  // Total Annual Value (cost savings + revenue increase)
  const totalAnnualValue = costSavings.total.annual + revenueImpact.total.annual;

  // Net Benefit
  const netAnnualBenefit = totalAnnualValue - annualRevferCost;

  // ROI Percentage
  const roiPercentage = (netAnnualBenefit / annualRevferCost) * 100;

  // ROI Multiplier
  const roiMultiplier = netAnnualBenefit / annualRevferCost;

  // Payback Period (in months)
  const monthlyBenefit = totalAnnualValue / 12;
  const monthlyRevferCost = CALCULATION_CONFIG.toolCosts.revferPremium;
  const netMonthlyBenefit = monthlyBenefit - monthlyRevferCost;
  const paybackMonths = monthlyRevferCost / netMonthlyBenefit;

  return {
    annualCost: annualRevferCost,
    totalValue: totalAnnualValue,
    netBenefit: netAnnualBenefit,
    percentage: roiPercentage,
    multiplier: roiMultiplier,
    paybackMonths: paybackMonths
  };
};

/**
 * Main ROI Calculation Function
 * Takes form inputs and returns complete ROI analysis INCLUDING SOCIAL MEDIA
 */
export const calculateROI = (formData) => {
  const { industry, revenue, messages, reviews, rating, socialPosts } = formData;

  // Calculate each component (existing)
  const timeSavings = calculateTimeSavings(messages, reviews);
  const costSavings = calculateCostSavings(timeSavings.total.monthly);
  const revenueImpact = calculateRevenueImpact(revenue, rating, reviews, messages, industry);
  
  // NEW: Calculate social media savings
  const socialMediaSavings = calculateSocialMediaSavings(socialPosts);
  const socialMediaRevenue = calculateSocialMediaRevenueImpact(revenue, socialPosts);

  // UPDATED: Combine time savings (add social media time)
  const combinedTimeSavings = {
    ...timeSavings,
    socialMedia: socialMediaSavings.time, // NEW
    total: {
      weekly: timeSavings.total.weekly + socialMediaSavings.time.weekly.saved,
      monthly: timeSavings.total.monthly + socialMediaSavings.time.monthly.saved,
      annual: timeSavings.total.annual + socialMediaSavings.time.annual.saved
    }
  };

  // UPDATED: Combine cost savings (add social media cost savings)
  const combinedCostSavings = {
    ...costSavings,
    socialMedia: socialMediaSavings.cost, // NEW
    total: {
      monthly: costSavings.total.monthly + socialMediaSavings.cost.total.monthly,
      annual: costSavings.total.annual + socialMediaSavings.cost.total.annual
    }
  };

  // UPDATED: Combine revenue impact (add social media revenue)
  const combinedRevenueImpact = {
    ...revenueImpact,
    socialMedia: socialMediaRevenue, // NEW
    total: {
      monthly: revenueImpact.total.monthly + socialMediaRevenue.monthly,
      annual: revenueImpact.total.annual + socialMediaRevenue.annual
    }
  };

  // UPDATED: Calculate ROI with social media included
  const roi = calculateTotalROI(combinedCostSavings, combinedRevenueImpact);

  return {
    inputs: formData,
    timeSavings: combinedTimeSavings,
    costSavings: combinedCostSavings,
    revenueImpact: combinedRevenueImpact,
    socialMedia: { // NEW: Detailed social media breakdown
      savings: socialMediaSavings,
      revenue: socialMediaRevenue
    },
    roi
  };
};

/**
 * Calculate Social Media Content Generation Savings
 * UPDATED: Now accepts postsPerWeek from user input instead of industry default
 */
export const calculateSocialMediaSavings = (postsPerWeek) => {
  const config = CALCULATION_CONFIG.socialMediaContent;

  // If user selected 0 posts, return zero savings
  if (postsPerWeek === 0) {
    return {
      time: {
        perPost: { manual: 0, ai: 0, saved: 0 },
        weekly: { manual: 0, ai: 0, saved: 0 },
        monthly: { manual: 0, ai: 0, saved: 0 },
        annual: { manual: 0, ai: 0, saved: 0 }
      },
      cost: {
        labor: { monthly: 0, annual: 0 },
        tools: { 
          monthly: 0, // If not posting, still save tool costs if they had them
          annual: 0 
        },
        total: { monthly: 0, annual: 0 }
      },
      posts: {
        perWeek: 0,
        monthly: 0,
        annual: 0
      },
      efficiency: {
        timeReductionPercent: 0,
        postsPerHour: { manual: 0, ai: 0 }
      }
    };
  }

  // Time savings per post
  const manualTimePerPost = config.manualTimePerPost / 60; // convert to hours
  const aiTimePerPost = config.aiTimePerPost / 60; // convert to hours
  const timeSavedPerPost = manualTimePerPost - aiTimePerPost;

  // Weekly/Monthly/Annual calculations
  const weeklyManualHours = (config.manualTimePerPost * postsPerWeek) / 60;
  const weeklyAIHours = (config.aiTimePerPost * postsPerWeek) / 60;
  const weeklyTimeSaved = weeklyManualHours - weeklyAIHours;
  
  const monthlyManualHours = weeklyManualHours * 4.33;
  const monthlyAIHours = weeklyAIHours * 4.33;
  const monthlyTimeSaved = weeklyTimeSaved * 4.33;
  
  const annualManualHours = weeklyManualHours * 52;
  const annualAIHours = weeklyAIHours * 52;
  const annualTimeSaved = weeklyTimeSaved * 52;

  // Cost savings - Labor (owner time or staff time)
  const hourlyLaborCost = 
    CALCULATION_CONFIG.laborCosts.partTimeHourlyRate * 
    CALCULATION_CONFIG.laborCosts.benefitsMultiplier;
  
  const monthlyLaborSavings = monthlyTimeSaved * hourlyLaborCost;
  const annualLaborSavings = monthlyLaborSavings * 12;

  // Cost savings - Tools
  const monthlyToolSavings = 
    CALCULATION_CONFIG.socialMediaCosts.totalWithoutRevfer - 
    CALCULATION_CONFIG.toolCosts.revferPremium;
  const annualToolSavings = monthlyToolSavings * 12;

  // Total savings
  const totalMonthlySavings = monthlyLaborSavings + monthlyToolSavings;
  const totalAnnualSavings = annualLaborSavings + annualToolSavings;

  // Post counts
  const monthlyPosts = postsPerWeek * 4.33;
  const annualPosts = postsPerWeek * 52;

  return {
    time: {
      perPost: {
        manual: manualTimePerPost,
        ai: aiTimePerPost,
        saved: timeSavedPerPost
      },
      weekly: {
        manual: weeklyManualHours,
        ai: weeklyAIHours,
        saved: weeklyTimeSaved
      },
      monthly: {
        manual: monthlyManualHours,
        ai: monthlyAIHours,
        saved: monthlyTimeSaved
      },
      annual: {
        manual: annualManualHours,
        ai: annualAIHours,
        saved: annualTimeSaved
      }
    },
    cost: {
      labor: {
        monthly: monthlyLaborSavings,
        annual: annualLaborSavings
      },
      tools: {
        monthly: monthlyToolSavings,
        annual: annualToolSavings
      },
      total: {
        monthly: totalMonthlySavings,
        annual: totalAnnualSavings
      }
    },
    posts: {
      perWeek: postsPerWeek,
      monthly: monthlyPosts,
      annual: annualPosts
    },
    efficiency: {
      timeReductionPercent: config.timeReductionRate * 100,
      postsPerHour: {
        manual: 1 / manualTimePerPost,
        ai: 1 / aiTimePerPost
      }
    }
  };
};

/**
 * Calculate Social Media Revenue Impact
 */
export const calculateSocialMediaRevenueImpact = (monthlyRevenue, postsPerWeek = 0) => {
  const config = CALCULATION_CONFIG.socialMediaImpact;

   // If not posting, no revenue impact from social media
   if (postsPerWeek === 0) {
    return {
      monthly: 0,
      annual: 0,
      percentage: 0,
      range: { low: 0, average: 0, high: 0 },
      note: 'No social media posting'
    };
  }

   // Scale revenue boost based on posting frequency
  // Less than 3x/week = lower impact, 5-7x/week = full impact, more than 7x = full impact
  let frequencyMultiplier = 1.0;
  if (postsPerWeek < 3) {
    frequencyMultiplier = 0.5; // 50% of full impact
  } else if (postsPerWeek < 5) {
    frequencyMultiplier = 0.75; // 75% of full impact
  }
  // 5+ posts = 100% impact

  // Use conservative 10% estimate, scaled by frequency
  const revenueBoost = config.revenueBoostConservative * frequencyMultiplier;

  const monthlyIncrease = monthlyRevenue * revenueBoost;
  const annualIncrease = monthlyIncrease * 12;

  return {
    monthly: monthlyIncrease,
    annual: annualIncrease,
    percentage: revenueBoost * 100,
    range: {
      low: monthlyRevenue * config.revenueBoostConservative * frequencyMultiplier * 12,
      average: monthlyRevenue * config.revenueBoostAverage * frequencyMultiplier * 12,
      high: monthlyRevenue * config.revenueBoostHigh * frequencyMultiplier * 12
    },
    frequencyMultiplier: frequencyMultiplier,
    postsPerWeek: postsPerWeek
  };
};

